import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewEncapsulation,
    ElementRef,
    ViewChild,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { DragScrollDirective } from 'app/modules/shared/directives/drag-scroll.directive';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { MenuCategory } from 'app/models/menu-category';
import { Establishment } from 'app/models/establishment';
import { LanguagesUtils } from 'app/utils/languages.utils';
import { MenuAnalyticsService } from 'app/services/menu-analytics.service';

/**
 * Margin, in pixels, within which a section counts as reached by the chips bar. A smooth scroll to
 * a section stops up to a fraction of a pixel short of its target (the browser snaps scroll
 * positions to device pixels): without it the tapped section stayed just under the bar and the
 * previous chip was selected again.
 */
export const ACTIVE_SECTION_TOLERANCE_PX = 2;

/**
 * Quiet time, in ms, after which the scroll started by a chip tap is considered over. The
 * scroll-based chip selection stays off until then, so the tapped chip remains selected, even when
 * the page cannot bring its section up to the bar (last sections of the menu).
 */
export const CHIP_SCROLL_SETTLE_MS = 150;

@Component({
    selector: 'app-category-nav-bar',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        DragScrollDirective,
        SearchComponent,
    ],
    templateUrl: './category-nav-bar.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class CategoryNavBarComponent implements OnChanges {
    @Input() categories: MenuCategory[] = [];
    @Input() menuTitle: string = '';
    @Input() selectedCategoryUuid: string | null = null;
    @Input() visible: boolean = true;
    @Input() establishment: Establishment;
    @Input() showSearch: boolean = false;

    @Output() categorySelected = new EventEmitter<string>();

    @ViewChild('chipsContainer') chipsContainer: ElementRef<HTMLElement>;
    @ViewChild(SearchComponent) private searchComponent: SearchComponent;

    constructor(private _menuAnalytics: MenuAnalyticsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && !changes['visible'].currentValue) {
            this.searchComponent?.close();
        }
    }

    getCategoryName(category: MenuCategory): string {
        return category.values.find(
            (value) => value.languageRefCode === LanguagesUtils.getMenuRemoteLang(this.establishment),
        )?.name || '';
    }

    onChipClick(categoryUuid: string): void {
        this.categorySelected.emit(categoryUuid);

        // A section consultation is a tap on its chip. Scroll-based section detection is
        // deliberately NOT instrumented: it fires continuously, expresses no intent, and the first
        // section is selected without any user action at load.
        this._menuAnalytics.trackCategoryTap(categoryUuid);
    }

    /**
     * Smoothly scrolls the chip container so the active chip is centered.
     * Always centers — not just when out of view — matching NordQR behavior.
     */
    scrollChipIntoView(categoryUuid: string): void {
        setTimeout(() => {
            const chipEl = document.getElementById(categoryUuid + '-chip');
            if (!chipEl) return;

            const container = this.chipsContainer?.nativeElement;
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const chipRect = chipEl.getBoundingClientRect();

            // Center the chip in the container
            const targetScroll = chipRect.left - containerRect.left + container.scrollLeft
                - (containerRect.width / 2) + (chipRect.width / 2);

            // No clamp: in RTL scrollLeft runs from 0 down to negative values, and the browser
            // bounds the target in both directions.
            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth',
            });
        }, 100);
    }
}
